import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Text,
  Badge,
  Spinner,
  Center,
  useColorModeValue,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { FiSearch, FiUser, FiBriefcase } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import StageBadge from './StageBadge';
import ContactTypeBadges from './ContactTypeBadges';
import { supabase, isSupabaseConfigured, transformDealToCamelCase, transformContactToCamelCase } from '../../utils/supabase';
import { useVisibilityScope } from '../../hooks/useVisibilityScope';
import { applyVisibilityScope, visibilityScopeKey } from '../../utils/recordVisibility';

/**
 * Entity Selector Component
 * 
 * Searchable modal for selecting deals OR contacts.
 * Used in Quick Actions and other workflows.
 * 
 * Features:
 * - Search with 500ms debounce
 * - Show recent items (last 10)
 * - Display entity type badges
 * - Performance optimized for large datasets
 */
export default function EntitySelector({
  entityType = 'both', // 'deal' | 'contact' | 'both'
  onSelect,
  preselected = null,
  userId = null, // Filter by specific user (for admins)
  excludeIds = [],
}) {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentDeals, setRecentDeals] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);

  const targetUserId = userId || currentUser?.uid;
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Multi-owner visibility: the target user's effective book (owned + team
  // book-shares + record shares), per entity type.
  const { scope: dealScope } = useVisibilityScope({ entityType: 'deal', targetUserId });
  const { scope: contactScope } = useVisibilityScope({ entityType: 'contact', targetUserId });
  const scopesKey = `${visibilityScopeKey(dealScope)}|${visibilityScopeKey(contactScope)}:g${(dealScope?.grantsVersion || 0) + (contactScope?.grantsVersion || 0)}`;

  // FIXED: Stabilize excludeIds array to prevent infinite loops
  // Create a string signature to detect content changes
  const excludeIdsSignature = excludeIds.join(',');
  const stableExcludeIds = useMemo(() => {
    return excludeIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeIdsSignature]);

  const loadRecentItems = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    
    setLoadingRecent(true);
    try {
      // Load recent deals if applicable
      if (entityType === 'deal' || entityType === 'both') {
        let recentDealsQuery = supabase
          .from('crm_deals')
          .select('*, crm_contacts!contact_id(id, first_name, last_name, email, phone)')
          .order('updated_at', { ascending: false })
          .limit(10);
        recentDealsQuery = applyVisibilityScope(recentDealsQuery, dealScope);
        const { data: dealsData, error: dealsError } = await recentDealsQuery;

        if (!dealsError && dealsData) {
          const deals = dealsData
            .filter(d => !stableExcludeIds.includes(d.id))
            .map(d => {
              const deal = transformDealToCamelCase(d);
              if (d.crm_contacts) {
                deal.contact = transformContactToCamelCase(d.crm_contacts);
              }
              return deal;
            });
          setRecentDeals(deals);
        }
      }

      // Load recent contacts if applicable
      if (entityType === 'contact' || entityType === 'both') {
        let recentContactsQuery = supabase
          .from('view_contact_summary')
          .select('*')
          .order('last_interaction_at', { ascending: false, nullsFirst: false })
          .limit(10);
        recentContactsQuery = applyVisibilityScope(recentContactsQuery, contactScope);
        const { data: contactsData, error: contactsError } = await recentContactsQuery;

        if (!contactsError && contactsData) {
          const contacts = contactsData
            .filter(c => !stableExcludeIds.includes(c.id))
            .map(transformContactToCamelCase);
          setRecentContacts(contacts);
        }
      }
    } catch (error) {
      console.error('Error loading recent items:', error);
    } finally {
      setLoadingRecent(false);
    }
    // scopesKey covers dealScope/contactScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId, entityType, stableExcludeIds, scopesKey]);

  // Load recent items on mount
  useEffect(() => {
    loadRecentItems();
  }, [loadRecentItems]);

  // Search function
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    const search = async () => {
      if (!isSupabaseConfigured()) return;
      
      setSearching(true);
      try {
        const searchLower = searchTerm.toLowerCase().trim();
        const results = [];

        // Search deals
        if (entityType === 'deal' || entityType === 'both') {
          let dealSearchQuery = supabase
            .from('crm_deals')
            .select('*, crm_contacts!contact_id(id, first_name, last_name, email, phone)')
            .or(`deal_name.ilike.%${searchLower}%,product.ilike.%${searchLower}%,source_medium.ilike.%${searchLower}%`)
            .order('created_at', { ascending: false })
            .limit(30);
          dealSearchQuery = applyVisibilityScope(dealSearchQuery, dealScope);
          const { data: dealsData, error: dealsError } = await dealSearchQuery;

          if (!dealsError && dealsData) {
            dealsData.forEach(d => {
              if (stableExcludeIds.includes(d.id)) return;
              const deal = transformDealToCamelCase(d);
              if (d.crm_contacts) {
                deal.contact = transformContactToCamelCase(d.crm_contacts);
              }
              results.push({ type: 'deal', data: deal });
            });
          }
        }

        // Search contacts
        if (entityType === 'contact' || entityType === 'both') {
          let contactSearchQuery = supabase
            .from('view_contact_summary')
            .select('*')
            .or(`first_name.ilike.%${searchLower}%,last_name.ilike.%${searchLower}%,email.ilike.%${searchLower}%,phone.ilike.%${searchLower}%`)
            .limit(30);
          contactSearchQuery = applyVisibilityScope(contactSearchQuery, contactScope);
          const { data: contactsData, error: contactsError } = await contactSearchQuery;

          if (!contactsError && contactsData) {
            contactsData.forEach(c => {
              if (stableExcludeIds.includes(c.id)) return;
              results.push({ type: 'contact', data: transformContactToCamelCase(c) });
            });
          }
        }

        setSearchResults(results.slice(0, 20));
      } catch (error) {
        console.error('Error searching:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    const debounceTimer = setTimeout(search, 500);
    return () => clearTimeout(debounceTimer);
    // scopesKey covers dealScope/contactScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, targetUserId, entityType, stableExcludeIds, scopesKey]);

  const EntityItem = ({ entity }) => {
    const isDeal = entity.type === 'deal';
    const itemData = entity.data;

    return (
      <Box
        p={3}
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
        _hover={{ bg: hoverBg, cursor: 'pointer' }}
        onClick={() => onSelect && onSelect(entity)}
      >
        <HStack spacing={3}>
          <Icon 
            as={isDeal ? FiBriefcase : FiUser} 
            boxSize={5} 
            color={isDeal ? 'blue.500' : 'purple.500'} 
          />
          <VStack align="flex-start" spacing={0} flex={1}>
            <HStack spacing={2}>
              <Text fontWeight="semibold" fontSize="sm">
                {isDeal 
                  ? itemData.dealName || 'Unnamed Deal'
                  : `${itemData.firstName} ${itemData.lastName}`
                }
              </Text>
              <Badge colorScheme={isDeal ? 'blue' : 'purple'} size="xs">
                {isDeal ? 'Deal' : 'Contact'}
              </Badge>
            </HStack>
            
            {isDeal ? (
              <HStack spacing={2} flexWrap="wrap">
                <StageBadge stage={itemData.stage} />
                {itemData.contact && (
                  <Text fontSize="xs" color="gray.600">
                    {itemData.contact.email || itemData.contact.phone}
                  </Text>
                )}
              </HStack>
            ) : (
              <HStack spacing={2} flexWrap="wrap">
                <ContactTypeBadges
                  contactTypes={itemData.contactTypes || []}
                  primaryContactType={itemData.primaryContactType}
                  size="xs"
                  maxDisplay={2}
                />
                {itemData.email && (
                  <Text fontSize="xs" color="gray.600">{itemData.email}</Text>
                )}
              </HStack>
            )}
          </VStack>
        </HStack>
      </Box>
    );
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Search */}
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder={
            entityType === 'deal' 
              ? 'Search deals by name, source, or product...'
              : entityType === 'contact'
              ? 'Search contacts by name, email, or phone...'
              : 'Search deals or contacts...'
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
      </InputGroup>

      {/* Results */}
      <Tabs size="sm" colorScheme="blue" index={selectedTab} onChange={setSelectedTab}>
        <TabList>
          <Tab>
            Search Results
            {searchResults.length > 0 && (
              <Badge ml={2} colorScheme="blue">{searchResults.length}</Badge>
            )}
          </Tab>
          {entityType === 'both' || entityType === 'deal' ? (
            <Tab>
              Recent Deals
              {recentDeals.length > 0 && (
                <Badge ml={2} colorScheme="gray">{recentDeals.length}</Badge>
              )}
            </Tab>
          ) : null}
          {entityType === 'both' || entityType === 'contact' ? (
            <Tab>
              Recent Contacts
              {recentContacts.length > 0 && (
                <Badge ml={2} colorScheme="gray">{recentContacts.length}</Badge>
              )}
            </Tab>
          ) : null}
        </TabList>

        <TabPanels>
          {/* Search Results */}
          <TabPanel px={0}>
            <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
              {searching ? (
                <Center py={8}>
                  <Spinner size="sm" />
                </Center>
              ) : searchTerm && searchTerm.trim().length < 3 ? (
                <Center py={8}>
                  <Text color="gray.500" fontSize="sm">
                    Type at least 3 characters to search...
                  </Text>
                </Center>
              ) : searchResults.length > 0 ? (
                searchResults.map((entity, idx) => (
                  <EntityItem key={`${entity.type}-${entity.data.id || idx}`} entity={entity} />
                ))
              ) : searchTerm ? (
                <Center py={8}>
                  <Text color="gray.500" fontSize="sm">No results found</Text>
                </Center>
              ) : (
                <Center py={8}>
                  <Text color="gray.500" fontSize="sm">
                    Start typing to search...
                  </Text>
                </Center>
              )}
            </VStack>
          </TabPanel>

          {/* Recent Deals */}
          {(entityType === 'both' || entityType === 'deal') && (
            <TabPanel px={0}>
              <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
                {loadingRecent ? (
                  <Center py={8}>
                    <Spinner size="sm" />
                  </Center>
                ) : recentDeals.length === 0 ? (
                  <Center py={8}>
                    <Text color="gray.500" fontSize="sm">No recent deals</Text>
                  </Center>
                ) : (
                  recentDeals.map(deal => (
                    <EntityItem 
                      key={deal.id} 
                      entity={{ type: 'deal', data: deal }} 
                    />
                  ))
                )}
              </VStack>
            </TabPanel>
          )}

          {/* Recent Contacts */}
          {(entityType === 'both' || entityType === 'contact') && (
            <TabPanel px={0}>
              <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
                {loadingRecent ? (
                  <Center py={8}>
                    <Spinner size="sm" />
                  </Center>
                ) : recentContacts.length === 0 ? (
                  <Center py={8}>
                    <Text color="gray.500" fontSize="sm">No recent contacts</Text>
                  </Center>
                ) : (
                  recentContacts.map(contact => (
                    <EntityItem 
                      key={contact.id} 
                      entity={{ type: 'contact', data: contact }} 
                    />
                  ))
                )}
              </VStack>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </VStack>
  );
}

